/**
 * usePushNotifications
 * – Registers the service worker
 * – Requests Notification permission
 * – Subscribes the browser to Web Push and saves the subscription to the backend
 * – Listens for OPEN_TASK messages posted by the SW on notification click
 *
 * Call this once per view that should receive phone push notifications
 * (MobileWorkerView, DashboardLayout).
 */
import { useEffect, useRef } from 'react';
import { apiFetch } from '../lib/apiFetch';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

interface UsePushOptions {
  /** Current logged-in user id */
  userId: string | undefined;
  /** Called when the user taps a push notification for a task */
  onOpenTask?: (taskId: string) => void;
}

export function usePushNotifications({ userId, onOpenTask }: UsePushOptions) {
  const subscribedRef = useRef(false);

  useEffect(() => {
    if (!userId || subscribedRef.current) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    let swReg: ServiceWorkerRegistration | null = null;

    async function setup() {
      try {
        // 1. Get VAPID public key from server
        const keyRes = await apiFetch(`${API_BASE}/push/vapid-public-key`);
        if (!keyRes.ok) return;
        const { key } = await keyRes.json();
        if (!key) return; // Push not configured on server

        // 2. Request permission
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;

        // 3. Register service worker
        swReg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
        await navigator.serviceWorker.ready;

        // 4. Subscribe to push
        let sub = await swReg.pushManager.getSubscription();
        if (!sub) {
          sub = await swReg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(key),
          });
        }

        // 5. Save subscription to backend
        await apiFetch(`${API_BASE}/push/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, subscription: sub.toJSON() }),
        });

        subscribedRef.current = true;
      } catch (err) {
        // Push setup is non-critical; fail silently
        console.warn('[push] setup failed:', err);
      }
    }

    setup();

    // Listen for OPEN_TASK messages from the service worker (background click)
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'OPEN_TASK' && event.data.taskId && onOpenTask) {
        onOpenTask(event.data.taskId);
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);

    // Also handle URL param set by SW when opening a new window
    const params = new URLSearchParams(window.location.search);
    const openTaskId = params.get('openTaskId');
    if (openTaskId && onOpenTask) {
      onOpenTask(openTaskId);
      // Clean up URL
      const url = new URL(window.location.href);
      url.searchParams.delete('openTaskId');
      window.history.replaceState({}, '', url.toString());
    }

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handler);
    };
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps
}

// ----- util -----

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}
